import React from "react";
import { ModeRendererProps } from "./types";

export function VasarelyTurrellMode({ brand }: ModeRendererProps) {
  // Security as illumination; clarity by light
  // Light chambers, gradients, voids
  
  return (
    <g>
      {/* Deep background for light effect */}
      <rect x="0" y="0" width="794" height="1123" fill={brand.deep} />
      
      {/* Central light chamber - Turrell's Ganzfeld effect */}
      <defs>
        <radialGradient id="turrell-glow-1" cx="50%" cy="50%">
          <stop offset="0%" stopColor={brand.aqua} stopOpacity="0.9" />
          <stop offset="40%" stopColor={brand.violet} stopOpacity="0.6" />
          <stop offset="80%" stopColor={brand.deep} stopOpacity="0.2" />
          <stop offset="100%" stopColor={brand.deep} stopOpacity="0" />
        </radialGradient>
        
        <radialGradient id="turrell-glow-2" cx="50%" cy="50%">
          <stop offset="0%" stopColor={brand.amber} stopOpacity="0.8" />
          <stop offset="50%" stopColor={brand.violet} stopOpacity="0.4" />
          <stop offset="100%" stopColor={brand.deep} stopOpacity="0" />
        </radialGradient>
        
        <linearGradient id="turrell-horizon" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={brand.violet} stopOpacity="0.6" />
          <stop offset="50%" stopColor={brand.aqua} stopOpacity="0.4" />
          <stop offset="100%" stopColor={brand.amber} stopOpacity="0.3" />
        </linearGradient>
      </defs>
      
      {/* Main light chamber */}
      <rect
        x="150"
        y="120"
        width="494"
        height="360"
        fill="url(#turrell-glow-1)"
        opacity={0.9}
      />
      
      {/* Nested light rectangles - layered illumination */}
      <rect x="200" y="160" width="394" height="280" fill={brand.aqua} opacity={0.15} />
      <rect x="250" y="200" width="294" height="200" fill={brand.violet} opacity={0.2} />
      <rect x="300" y="240" width="194" height="120" fill={brand.amber} opacity={0.25} />
      
      {/* Vasarely optical structure beneath light */}
      {Array.from({ length: 12 }).map((_, i) => {
        const size = 340 - i * 28;
        const offset = i * 14;
        return (
          <rect
            key={`structure-${i}`}
            x={227 + offset}
            y={160 + offset}
            width={size}
            height={size}
            fill="none"
            stroke={brand.white}
            strokeWidth="0.5"
            opacity={0.1}
          />
        );
      })}
      
      {/* Light void - negative space */}
      <ellipse
        cx="650"
        cy="280"
        rx="100"
        ry="140"
        fill="url(#turrell-glow-2)"
        opacity={0.7}
      />
      
      {/* Horizon gradient field */}
      <rect
        x="80"
        y="540"
        width="634"
        height="280"
        fill="url(#turrell-horizon)"
        opacity={0.6}
      />
      
      {/* Subtle geometric structure in horizon */}
      {Array.from({ length: 8 }).map((_, i) => {
        const y = 560 + i * 32;
        return (
          <line
            key={`horizon-${i}`}
            x1="80"
            y1={y}
            x2="714"
            y2={y}
            stroke={brand.white}
            strokeWidth="0.3"
            opacity={0.2}
          />
        );
      })}
      
      {/* Light portals - bottom section */}
      {[
        { cx: 180, cy: 920, rx: 70, ry: 50, color: brand.aqua },
        { cx: 397, cy: 920, rx: 80, ry: 60, color: brand.violet },
        { cx: 614, cy: 920, rx: 70, ry: 50, color: brand.amber },
      ].map((portal, i) => (
        <g key={`portal-${i}`}>
          <ellipse
            cx={portal.cx}
            cy={portal.cy}
            rx={portal.rx}
            ry={portal.ry}
            fill={portal.color}
            opacity={0.4}
          />
          {Array.from({ length: 5 }).map((_, j) => (
            <ellipse
              key={`ring-${j}`}
              cx={portal.cx}
              cy={portal.cy}
              rx={portal.rx - j * 12}
              ry={portal.ry - j * 9}
              fill="none"
              stroke={brand.white}
              strokeWidth="0.5"
              opacity={0.2}
            />
          ))}
        </g>
      ))}
      
      {/* Atmospheric glow particles */}
      {Array.from({ length: 60 }).map((_, i) => {
        const x = 100 + (Math.sin(i * 2.5) * 300 + 300);
        const y = 150 + (Math.cos(i * 1.8) * 200 + 200);
        const r = 1 + Math.sin(i * 0.7) * 2;
        
        return (
          <circle
            key={`particle-${i}`}
            cx={x}
            cy={y}
            r={r}
            fill={brand.white}
            opacity={0.3}
          />
        );
      })}
      
      {/* Bottom illumination field */}
      <rect
        x="80"
        y="1000"
        width="634"
        height="80"
        fill={brand.aqua}
        opacity={0.1}
      />
      
      {Array.from({ length: 20 }).map((_, i) => {
        const x = 80 + i * 32;
        return (
          <line
            key={`light-ray-${i}`}
            x1={x}
            y1="1000"
            x2={x + 10}
            y2="1080"
            stroke={brand.white}
            strokeWidth="0.5"
            opacity={0.2}
          />
        );
      })}
    </g>
  );
}
